import {
  HwzForumFeedApiResponse,
  HwzForumFeedThread,
} from "@app/types/ServerCustomContext";
import { configHome, DataSource } from "@pub/config";
import placeholders from "@pub/placeholders";
import {
  Ads,
  ResponsiveImage,
  SectionSubHeading,
  Typography,
} from "@sphtech/dmg-design-system";
import {
  extractHomeContentData,
  HomeLayoutProps,
} from "@src/app/layouts/Home/Elements/utils";
import styles from "@src/app/layouts/Home/homelayout.module.css";
import cx from "classnames";
import { ReactNode } from "react";
import useSWR from "swr";

import hwzForumChatterStyle from "./HwzForumChatter.module.css";

type Props = {
  data: HomeLayoutProps["data"];
  dataSource: DataSource;
};

const updatedTimeAgo = (datestring?: string): string => {
  if (!datestring || !/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(datestring))
    return "";
  const isoDateString = `${datestring.replace(" ", "T")}+08:00`;
  const date = new Date(isoDateString);
  const thistime = new Date().getTime();
  const lasttime = date.getTime();
  const minutesDiff = Math.floor((thistime - lasttime) / 1000 / 60);
  if (minutesDiff < 2) return "Updated a minute ago";
  if (minutesDiff < 60) return `Updated ${minutesDiff} mins ago`;
  if (minutesDiff < 120) return "Updated an hour ago";
  if (minutesDiff < 1440)
    return `Updated ${Math.floor(minutesDiff / 60)} hours ago`;

  return `Updated on ${date.toLocaleString("en-SG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })}`;
};

const appendForumRefCode = (url: string): string => {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}ref=latestforumchatter`;
};

export default function HwzForumChatter(homedata: Props): ReactNode {
  const { data, isLoading } = useSWR<HwzForumFeedApiResponse>(
    "/_plat/api/hwz_forum_chatter",
  );

  const isHwzForumFeedThread = (data?: unknown): data is HwzForumFeedThread => {
    return (
      !!data &&
      typeof data === "object" &&
      "title" in data &&
      typeof data.title === "string" &&
      "url" in data &&
      typeof data.url === "string" &&
      "thread_id" in data &&
      typeof data.thread_id === "number" &&
      "last_post_date" in data &&
      typeof data.last_post_date === "string" &&
      "view_count" in data &&
      typeof data.view_count === "number"
    );
  };

  const homeForumData = extractHomeContentData(
    homedata.data,
    homedata.dataSource,
  );
  const homeForumItems = homeForumData?.items;

  return (
    <div
      className={cx(
        styles.bodyContainer,
        hwzForumChatterStyle.homeContentForumChatterContainer,
      )}
    >
      <SectionSubHeading title={homeForumData?.title || "Forums"} />
      <div className={cx(hwzForumChatterStyle.innerContainer)}>
        <div className={hwzForumChatterStyle.forumContainer}>
          {!isLoading &&
            !!data?.results?.length &&
            data.results.map((forum, index) => (
              <div key={index} className={hwzForumChatterStyle.forumRow}>
                <div className={hwzForumChatterStyle.forumImage}>
                  {homeForumItems
                    ?.filter((item) => item.image?.alt === forum.key)
                    .map(
                      (item, index) =>
                        item.image?.crops.landscape && (
                          <ResponsiveImage
                            {...item.image.crops.landscape}
                            key={index}
                            errorSrc={placeholders.landscape.src}
                          />
                        ),
                    )}
                </div>
                <div className={hwzForumChatterStyle.forumItem}>
                  <div className={hwzForumChatterStyle.forumTime}>
                    {forum.threads.length > 0 &&
                      updatedTimeAgo(forum.threads[0]?.last_post_date)}
                  </div>
                  <div className={hwzForumChatterStyle.forumTitle}>
                    <a
                      href={appendForumRefCode(forum.url)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {forum.title}
                    </a>
                  </div>
                  {forum.threads.map(
                    (thread, index) =>
                      isHwzForumFeedThread(thread) && (
                        <div
                          key={index}
                          className={hwzForumChatterStyle.forumThread}
                        >
                          <a
                            href={appendForumRefCode(thread.url || "")}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {thread.title}
                          </a>
                        </div>
                      ),
                  )}
                </div>
              </div>
            ))}
          <div className={hwzForumChatterStyle.forumLink}>
            <Typography.TextLink
              href={appendForumRefCode("https://forums.hardwarezone.com.sg")}
              target="_blank"
            >
              View more discussions {configHome.viewMore?.icon || ""}
            </Typography.TextLink>
          </div>
        </div>
        <div
          className={cx(styles.imuContainer, hwzForumChatterStyle.imuContainer)}
        >
          <Ads.AdsSideBar />
        </div>
      </div>
    </div>
  );
}
